/**
 * Local persistence for the side-scroller world (Brief §9 "permanent,
 * progressive persistence" - chopped/planted state carries forward
 * indefinitely, distinct from the season-scoped Tier 2 scoring economy).
 *
 * CP21 spec (`21-game-side-scroller-build.md` #1) calls for schema
 * `{ side, frontierPosition, actions: [{pos, type, at}] }` persisted "keyed
 * per side". Implemented here as a single localStorage entry containing a
 * `Record<Side, WorldSideState>` (one key per side, each shaped exactly as
 * specified) rather than two separate localStorage keys - documented in
 * `README.md`'s local-state <-> CP14-contract mapping section along with
 * why (atomic read/write of both sides avoids partial-write drift).
 *
 * Storage is accepted as a parameter (not read from `window.localStorage`
 * directly) so this module is unit-testable without a browser/jsdom
 * storage mock quirks - inject `window.localStorage` at the call site.
 */
import type { ActionType, PersistedWorldState, Side, WorldSideState } from './types';

export const WORLD_STORAGE_KEY = 'lothlorien-game.world.v1';

type Reader = Pick<Storage, 'getItem'>;
type Writer = Pick<Storage, 'setItem'>;

function emptySideState(side: Side): WorldSideState {
  return { side, frontierPosition: 0, actions: [] };
}

export function createDefaultWorldState(): PersistedWorldState {
  return {
    beornings: emptySideState('beornings'),
    lothwen: emptySideState('lothwen'),
  };
}

/** Narrow, defensive parse - falls back to the default state on any corruption rather than throwing. */
function coerceSideState(side: Side, value: unknown): WorldSideState {
  if (typeof value !== 'object' || value === null) return emptySideState(side);
  const v = value as Partial<WorldSideState>;
  const frontierPosition =
    typeof v.frontierPosition === 'number' && Number.isFinite(v.frontierPosition) && v.frontierPosition >= 0
      ? v.frontierPosition
      : 0;
  const actions = Array.isArray(v.actions) ? v.actions.filter(isWorldAction) : [];
  return { side, frontierPosition, actions };
}

function isWorldAction(value: unknown): value is WorldSideState['actions'][number] {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v.pos === 'number' && (v.type === 'chop' || v.type === 'plant') && typeof v.at === 'string';
}

export function loadWorldState(storage: Reader): PersistedWorldState {
  const raw = storage.getItem(WORLD_STORAGE_KEY);
  if (!raw) return createDefaultWorldState();
  try {
    const parsed = JSON.parse(raw) as Partial<Record<Side, unknown>>;
    return {
      beornings: coerceSideState('beornings', parsed.beornings),
      lothwen: coerceSideState('lothwen', parsed.lothwen),
    };
  } catch {
    return createDefaultWorldState();
  }
}

export function saveWorldState(storage: Writer, state: PersistedWorldState): void {
  storage.setItem(WORLD_STORAGE_KEY, JSON.stringify(state));
}

/**
 * Resolves the next tile on `side` (frontierPosition + 1), appends the
 * action, and returns a NEW state object (does not mutate the input) - the
 * caller is responsible for persisting it via `saveWorldState`.
 */
export function recordAction(
  state: PersistedWorldState,
  side: Side,
  type: ActionType,
  atIso: string,
): PersistedWorldState {
  const current = state[side];
  const pos = current.frontierPosition + 1;
  const updated: WorldSideState = {
    side,
    frontierPosition: pos,
    actions: [...current.actions, { pos, type, at: atIso }],
  };
  return { ...state, [side]: updated };
}
